import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DwConfigService, DwConfigServiceToken, DwEventService, DwOrmDataService, DwSecurityUserService } from '@devwareapps/devware-cap';
import { defer, merge, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, debounceTime, map, shareReplay, take, timeout } from 'rxjs/operators';
import { AppMetaData } from '../../../meta-data/app-meta-data.service';
import { CentralDataService } from '../../streaker-central/services/central-data.service';
import { BadgeBoardList, BadgeBoardStandings, BadgeCollection, BadgeSummaries } from '../models/badge.models';
import { BoardAudience, FullStanding, FullStandings, OwnFitnessStats } from '../models/leaderboard.models';

interface SummaryRead {
    key: string;
    enrollmentId?: number;
    teamId?: number;
    result: ReplaySubject<BadgeSummaries>;
    stream: Observable<BadgeSummaries>;
}

/** Short-lived collection sharing; every activity/account refresh expires published results. */
@Injectable({ providedIn: 'root' })
export class BadgeService {
    private changes = new Subject<void>();
    private _collections = new Map<string, { expires: number; stream: Observable<BadgeCollection> }>();
    private _summaries = new Map<string, { expires: number; stream: Observable<BadgeSummaries> }>();
    private _summaryQueue = new Map<string, { challengeId: number; revision: number; reads: SummaryRead[] }>();
    private _summaryTimer: ReturnType<typeof setTimeout>;
    private _standingsKeys = new Set<string>();
    private _standingsRevision = 0;
    readonly changed$ = this.changes.asObservable().pipe(debounceTime(0));
    constructor(private http: HttpClient, @Inject(DwConfigServiceToken) private config: DwConfigService,
        private security: DwSecurityUserService, events: DwEventService, private data: CentralDataService) {
        merge(security.securityContextChanged(), data.changed$, data.teamChanged$).subscribe(() => this.invalidate());
        [AppMetaData.FitnessActivity, AppMetaData.UserChallengeFriend, AppMetaData.UserProfile,
            AppMetaData.UserChallengeComment, AppMetaData.UserChallenge, AppMetaData.ChallengeTeamMember,
            AppMetaData.BadgeAward, AppMetaData.BadgeProgress,
            AppMetaData.ChallengeBadgeConfiguration, AppMetaData.ChallengeBadgeRule,
            AppMetaData.BadgeCalculationRun, AppMetaData.BadgeSubject, AppMetaData.LeaderboardEntry].forEach(item =>
            events.getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item.ItemDetail.itemName)).subscribe(() => this.invalidate()));
    }
    private invalidate(): void { this.expireCollections(); this.changes.next(); }
    refreshAwards(): void { this.invalidate(); }
    expireCollections(): void {
        this._collections.clear(); this._summaries.clear(); this._standingsRevision++;
        this._standingsKeys.forEach(key => this.data.expireSnapshot(key)); this._standingsKeys.clear();
    }
    private url(challengeId: number): string {
        return this.config.coreConfig.apiRoot.replace(/\/$/, '') + '/fitness/gamification/challenges/' + challengeId;
    }
    collection(challengeId: number, enrollmentId?: number, teamId?: number): Observable<BadgeCollection> {
        return defer(() => {
            const context = this.security.securityContext;
            const key = JSON.stringify([this.config.coreConfig.apiRoot, context?.ApiRoot, context?.AuthenticatedUser?.UserId,
                context?.ApplicationUser?.UserId, challengeId, enrollmentId || 0, teamId || 0]);
            const cached = this._collections.get(key);
            if (cached && cached.expires > Date.now()) { return cached.stream; }
            const params = new HttpParams().set(teamId ? 'teamId' : 'userChallengeId', String(teamId || enrollmentId));
            const stream = this.http.get<BadgeCollection>(this.url(challengeId) + '/badges', { params }).pipe(timeout(30000), take(1),
                catchError(error => { if (this._collections.get(key)?.stream === stream) { this._collections.delete(key); } return throwError(error); }),
                shareReplay({ bufferSize: 1, refCount: true }));
            this._collections.set(key, { expires: Date.now() + 60000, stream }); return stream;
        });
    }
    summary(challengeId: number, enrollmentId?: number, teamId?: number): Observable<BadgeCollection> {
        return this.summaries(challengeId, enrollmentId, teamId).pipe(map(result => {
            const subject = result.Subjects.find(row => teamId ? row.ChallengeTeamId === teamId : row.UserChallengeId === enrollmentId);
            return { State: result.State, ScoreThroughDate: result.ScoreThroughDate, PublishedDateTime: result.PublishedDateTime,
                IsFinal: result.IsFinal, TotalCount: subject?.TotalCount || 0,
                LeaderboardCount: subject?.LeaderboardCount ?? subject?.TotalCount ?? 0, AchievementCount: subject?.AchievementCount || 0,
                EarnedAwards: (subject?.EarnedAwards || []).map(award => ({...award})),
                Badges: subject ? subject.Badges.map(badge => ({...badge})) : [] };
        }));
    }
    private summaries(challengeId: number, enrollmentId?: number, teamId?: number): Observable<BadgeSummaries> {
        return defer(() => {
            if (!(teamId > 0 || enrollmentId > 0)) { return throwError(new Error('A badge subject is required.')); }
            const context = this.security.securityContext;
            const scope = JSON.stringify([this.config.coreConfig.apiRoot, context?.ApiRoot, context?.AuthenticatedUser?.UserId,
                context?.ApplicationUser?.UserId, challengeId, this._standingsRevision]);
            const key = JSON.stringify([scope, teamId ? 'team' : 'person', teamId || enrollmentId]);
            const cached = this._summaries.get(key);
            if (cached && cached.expires > Date.now()) { return cached.stream; }
            const result = new ReplaySubject<BadgeSummaries>(1);
            const stream = result.asObservable();
            this._summaries.set(key, { expires: Date.now() + 300000, stream });
            let batch = this._summaryQueue.get(scope);
            if (!batch) {
                batch = { challengeId, revision: this._standingsRevision, reads: [] };
                this._summaryQueue.set(scope, batch);
            }
            batch.reads.push({ key, enrollmentId: teamId ? undefined : enrollmentId, teamId, result, stream });
            if (!this._summaryTimer) { this._summaryTimer = setTimeout(() => this.flushSummaryQueue(), 0); }
            return stream;
        });
    }

    private flushSummaryQueue(): void {
        clearTimeout(this._summaryTimer); this._summaryTimer = undefined;
        const batches = Array.from(this._summaryQueue.values()); this._summaryQueue.clear();
        batches.forEach(batch => {
            for (let offset = 0; offset < batch.reads.length; offset += 100) {
                const reads = batch.reads.slice(offset, offset + 100);
                const fail = (error: unknown) => reads.forEach(read => {
                    if (this._summaries.get(read.key)?.stream === read.stream) { this._summaries.delete(read.key); }
                    read.result.error(error);
                });
                if (batch.revision !== this._standingsRevision) { fail(new Error('Badges changed while loading. Please retry.')); continue; }
                const body = { UserChallengeIds: reads.filter(read => read.enrollmentId).map(read => read.enrollmentId),
                    TeamIds: reads.filter(read => read.teamId).map(read => read.teamId) };
                this.http.post<BadgeSummaries>(this.url(batch.challengeId) + '/badge-summaries', body).pipe(
                    timeout(30000), take(1)).subscribe(response => {
                        if (batch.revision !== this._standingsRevision) { fail(new Error('Badges changed while loading. Please retry.')); return; }
                        reads.forEach(read => {
                            // Retain only this subject in its cache, not the entire page's response.
                            read.result.next({ ...response, Subjects: response.Subjects.filter(subject => read.teamId
                                ? subject.ChallengeTeamId === read.teamId : subject.UserChallengeId === read.enrollmentId) });
                            read.result.complete();
                        });
                    }, fail);
            }
        });
    }
    boards(challengeId: number): Observable<BadgeBoardList> { return this.http.get<BadgeBoardList>(this.url(challengeId) + '/leaderboards'); }
    standings(challengeId: number, ruleId: number): Observable<BadgeBoardStandings> {
        return this.http.get<BadgeBoardStandings>(this.url(challengeId) + '/leaderboards/' + ruleId);
    }
    fullStandings(challengeId: number, ruleId: number, page = 1, search = '', publicationId?: number,
        audience: BoardAudience = 'all', focusId?: number): Observable<FullStandings> {
        let params = new HttpParams().set('page', String(page)).set('pageSize', '25').set('search', search).set('audience', audience);
        if (publicationId) { params = params.set('publicationId', String(publicationId)); }
        if (focusId) { params = params.set('focusId', String(focusId)); }
        return defer(() => {
            // Cache only requested pages; unopened activities do not start any reads.
            const key = 'activity-standings:' + JSON.stringify([challengeId, ruleId, page, search, publicationId || 0, audience, focusId || 0]);
            const revision = this._standingsRevision;
            const expiresAt = Date.now() + 300000;
            if (!this._standingsKeys.has(key) && this._standingsKeys.size >= 200) {
                const oldest = this._standingsKeys.values().next().value;
                this.data.expireSnapshot(oldest); this._standingsKeys.delete(oldest);
            }
            this._standingsKeys.add(key);
            return this.data.snapshot(key, () => this.http.get<FullStandings>(this.url(challengeId) + '/leaderboards/' + ruleId + '/standings', {params})
                .pipe(timeout(30000), take(1), map(result => {
                    if (revision !== this._standingsRevision) { throw new Error('Standings changed while loading. Please retry.'); }
                    if (result.State !== 'ready') { this.data.expireSnapshot(key); }
                    return {...result, cacheExpiresAt: expiresAt};
                })), 300000).pipe(map(result => ({...result,
                    Rows: result.Rows.map(cloneStanding), Leaders: result.Leaders?.map(cloneStanding),
                    MyStanding: result.MyStanding ? cloneStanding(result.MyStanding) : undefined
                })));
        });
    }
    ownStats(challengeId: number, enrollmentId: number): Observable<OwnFitnessStats> {
        const root = this.config.coreConfig.apiRoot.replace(/\/$/, '');
        return this.http.get<OwnFitnessStats>(root + '/fitness/stats/challenges/' + challengeId + '/streakers/' + enrollmentId).pipe(timeout(30000), take(1));
    }
}

/** Views may clear a broken avatar path; never mutate the shared cached response. */
function cloneStanding(row: FullStanding): FullStanding { return {...row, Award: row.Award ? {...row.Award} : undefined}; }
